import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ChevronRight, Lock, Truck, ShieldCheck, Tag, CheckCircle2, Mail, MapPin, Download, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiCreateOrder } from '../services/auth';
import { validateCoupon } from '../services/api';
import AddressBook from './AddressBook';
import useSeo from '../hooks/useSeo';

export default function Checkout({ cart, onClearCart }) {
  const { user, isAuthed } = useAuth();
  useSeo({ title: 'Secure Checkout', description: 'Complete your order. Secure Razorpay + Cash on Delivery.', canonical: '/checkout' });
  const [step, setStep] = useState(1); // 1 = details, 2 = success
  const [orderId, setOrderId] = useState(null);
  const [coupon, setCoupon] = useState({ code: '', discount: 0, error: '', applied: null, busy: false });
  const [address, setAddress] = useState(null);   // the selected saved address
  const [payment, setPayment] = useState('cod');
  const [notes, setNotes] = useState('');

  // Login gate — user asked "before going to cart or checkout must login"
  if (!isAuthed) {
    return <Navigate to="/login" state={{ next: '/checkout' }} replace />;
  }

  const subtotal = cart.reduce((s, i) => s + i.price * (i.qty || 1), 0);
  const savings  = cart.reduce((s, i) => s + (i.originalPrice - i.price) * (i.qty || 1), 0);
  const discount = coupon.discount;
  const shipping = payment === 'cod' ? 0 : 0;
  const total    = Math.max(0, subtotal - discount + shipping);

  const applyCoupon = async () => {
    const code = (coupon.code || '').trim().toUpperCase();
    if (!code) { setCoupon(c => ({ ...c, error: 'Enter a coupon code.', discount: 0, applied: null })); return; }
    setCoupon(c => ({ ...c, busy: true, error: '' }));
    try {
      const res = await validateCoupon({ code, subtotal });
      if (!res.valid) { setCoupon(c => ({ ...c, busy: false, error: res.message, discount: 0, applied: null })); return; }
      setCoupon({ code, discount: res.discount, error: '', applied: res, busy: false });
    } catch (e) {
      setCoupon(c => ({ ...c, busy: false, error: e.message || 'Could not validate coupon.', discount: 0, applied: null }));
    }
  };

  const removeCoupon = () => setCoupon({ code: '', discount: 0, error: '', applied: null, busy: false });

  const buildPayload = (paymentStatus) => ({
    items: cart.map(it => ({
      product_id: it.id,
      name: it.title.split('|')[0],
      image: it.image,
      size: it.size,
      qty: it.qty || 1,
      unit_price: it.price,
      price: it.price,
    })),
    address_id: address?.id,
    shipping_address: address ? {
      first_name: address.first_name, last_name: address.last_name || '',
      phone: address.phone, email: address.email || user?.email || '',
      address_line1: address.address_line1, address_line2: address.address_line2 || '',
      city: address.city, state: address.state, pincode: address.pincode, country: address.country || 'India',
    } : null,
    payment_method: payment,
    payment_status: paymentStatus,
    status: paymentStatus === 'paid' ? 'confirmed' : 'pending',
    notes,
    coupon_code: coupon.applied?.code || null,
    subtotal, shipping, total, discount,
  });

  const finalize = async (paymentStatus) => {
    const created = await apiCreateOrder(buildPayload(paymentStatus), user?.id || null);
    setOrderId(created.order_number);
    setStep(2);
    if (onClearCart) onClearCart();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!address) { alert('Please select or add a delivery address.'); return; }
    if (payment === 'razorpay') {
      const ok = window.confirm(`Proceed to pay Rs. ${total.toLocaleString('en-IN')} via Razorpay?\n\n(Test-mode gateway — click OK to simulate success.)`);
      if (!ok) return;
      await finalize('paid');
    } else {
      await finalize('pending');
    }
  };

  if (cart.length === 0 && step === 1) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h1 className="font-serif text-3xl text-neutral-900">Your cart is empty</h1>
        <p className="mt-3 text-neutral-600">Add something beautiful before checking out.</p>
        <Link to="/collection/all" className="inline-block mt-6 bg-[#1e3a2b] hover:bg-[#2a5040] text-white px-8 py-3 rounded-md font-semibold transition-colors">
          Browse Collection
        </Link>
      </main>
    );
  }

  if (step === 2) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="w-20 h-20 rounded-full bg-[#1e3a2b]/10 grid place-items-center mx-auto">
          <CheckCircle2 className="w-10 h-10 text-[#1e3a2b]" />
        </div>
        <h1 className="mt-6 font-serif text-4xl text-neutral-900">Order confirmed!</h1>
        <p className="mt-3 text-neutral-600">Thank you {address?.first_name || user?.name?.split(' ')[0] || 'friend'} — your resin artistry is on the way.</p>
        <div className="mt-8 bg-[#f6f2ea] border border-neutral-200 rounded-xl p-6 text-left">
          <div className="flex items-center justify-between border-b border-neutral-200 pb-3 mb-3">
            <span className="text-sm text-neutral-600">Order number</span>
            <span className="font-mono font-semibold text-neutral-900">#{orderId}</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-neutral-500" /><span className="text-neutral-800">Confirmation sent to <b>{user?.email || address?.phone}</b></span></div>
            {address && (
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-neutral-500" /><span className="text-neutral-800">{address.address_line1}, {address.city}, {address.state} {address.pincode}</span></div>
            )}
            {/* <div className="flex items-start gap-2"><Truck className="w-4 h-4 mt-0.5 text-neutral-500" /><span className="text-neutral-800">Expected delivery in 10-12 days</span></div> */}
          </div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          {user && (
            // <Link to={`/account/orders/${orderId}`} className="bg-[#1e3a2b] hover:bg-[#2a5040] text-white px-6 py-3 rounded-md font-semibold transition-colors inline-flex items-center gap-2">
            //   <Download className="w-4 h-4" /> View order & invoice
            // </Link>

            <Link to={`/account/orders`} className="bg-[#1e3a2b] hover:bg-[#2a5040] text-white px-6 py-3 rounded-md font-semibold transition-colors inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> View order & invoice
            </Link>

            

          )}
          <Link to="/" className="border border-[#1e3a2b] text-[#1e3a2b] hover:bg-[#1e3a2b] hover:text-white px-6 py-3 rounded-md font-semibold transition-colors">Back to Home</Link>
          <Link to="/collection/all" className="border border-neutral-300 text-neutral-700 hover:bg-neutral-100 px-6 py-3 rounded-md font-semibold transition-colors">Continue Shopping</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#f6f2ea] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        <nav className="flex items-center gap-1.5 text-xs text-neutral-500">
          <Link to="/" className="hover:text-[#1e3a2b]">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#1e3a2b] font-medium">Checkout</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-[1fr_420px] gap-8">
        {/* Left: form */}
        <form onSubmit={submit} className="space-y-6">
          <h1 className="font-serif text-3xl sm:text-4xl text-neutral-900">Checkout</h1>

          {/* Shipping — pick from saved addresses */}
          <section className="bg-white rounded-xl border border-neutral-200 p-5 sm:p-6">
            <AddressBook onSelect={setAddress} selectedId={address?.id} />
          </section>

          {/* Payment */}
          <section className="bg-white rounded-xl border border-neutral-200 p-5 sm:p-6">
            <h2 className="font-semibold text-neutral-900 mb-4">Payment method</h2>
            <div className="space-y-3">
              {[
                { v: 'cod',      l: 'Cash on Delivery',                              d: '+ Rs. 0 handling fee. Pay in cash when your order arrives.' },
                // { v: 'razorpay', l: 'Razorpay (UPI / Card / Netbanking / Wallet)',   d: 'Pay securely online — order is confirmed instantly.' },
              ].map(p => (
                <label key={p.v} data-testid={`payment-${p.v}`} className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${payment === p.v ? 'border-[#1e3a2b] bg-[#f6f2ea]' : 'border-neutral-200 hover:border-neutral-400'}`}>
                  <input
                    type="radio"
                    name="payment"
                    value={p.v}
                    checked={payment === p.v}
                    onChange={(e) => setPayment(e.target.value)}
                    className="mt-1 accent-[#1e3a2b]"
                  />
                  <div>
                    <div className="font-medium text-neutral-900">{p.l}</div>
                    <div className="text-xs text-neutral-500">{p.d}</div>
                  </div>
                </label>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-xl border border-neutral-200 p-5 sm:p-6">
            <label className="text-xs font-medium text-neutral-600">Order notes (optional)</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Delivery instructions, gift message..."
              className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
            />
          </section>

          <button
            type="submit"
            data-testid="place-order-btn"
            disabled={!address}
            className={`w-full font-bold py-4 rounded-md tracking-wide transition-colors flex items-center justify-center gap-2 ${
              !address ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed' : 'bg-[#1e3a2b] hover:bg-[#2a5040] text-white'
            }`}
          >
            <Lock className="w-5 h-5" /> {!address ? 'Select an address to continue' : `${payment === 'razorpay' ? 'Pay' : 'Place Order'} • Rs. ${total.toLocaleString('en-IN')}.00`}
          </button>
        </form>

        {/* Right: summary */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="bg-white rounded-xl border border-neutral-200 p-5 sm:p-6">
            <h3 className="font-serif text-xl text-neutral-900 mb-4">Order Summary</h3>
            <div className="space-y-4 max-h-72 overflow-y-auto">
              {cart.map((it, i) => (
                <div key={i} className="flex gap-3">
                  <div className="relative shrink-0">
                    <img src={it.image} alt="" className="w-16 h-16 rounded-md object-cover" />
                    <span className="absolute -top-1 -right-1 bg-neutral-800 text-white text-xs font-semibold rounded-full w-5 h-5 grid place-items-center">{it.qty || 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-neutral-900 line-clamp-2">{it.title.split('|')[0]}</div>
                    <div className="text-xs text-neutral-500">{it.size}</div>
                  </div>
                  <div className="text-right text-sm">
                    <div className="font-semibold text-neutral-900">Rs. {(it.price * (it.qty || 1)).toLocaleString('en-IN')}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-neutral-200">
              {/* Coupon input */}
              {coupon.applied ? (
                <div className="flex items-center justify-between bg-[#f6f2ea] border border-[#b8935a]/40 rounded-md px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[#b8935a]" />
                    <div>
                      <div className="font-semibold text-[#1e3a2b]">{coupon.applied.code}</div>
                      <div className="text-xs text-neutral-600">-Rs. {coupon.discount.toLocaleString('en-IN')} applied</div>
                    </div>
                  </div>
                  <button type="button" onClick={removeCoupon} className="p-1 rounded hover:bg-white" aria-label="Remove coupon">
                    <X className="w-4 h-4 text-neutral-500" />
                  </button>
                </div>
              ) : (
                <div>
                  <label className="text-xs font-medium text-neutral-600 flex items-center gap-1"><Tag className="w-3 h-3" /> Have a coupon?</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      value={coupon.code}
                      onChange={(e) => setCoupon(c => ({ ...c, code: e.target.value, error: '' }))}
                      placeholder="Enter code"
                      className="flex-1 border border-neutral-300 rounded-md px-3 py-2 uppercase text-sm focus:outline-none focus:border-[#1e3a2b]"
                    />
                    <button
                      type="button"
                      onClick={applyCoupon}
                      className="bg-[#1e3a2b] hover:bg-[#2a5040] text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
                    >
                      Apply
                    </button>
                  </div>
                  {coupon.error && <p className="mt-1 text-xs text-red-600">{coupon.error}</p>}
                  <p className="mt-1 text-[11px] text-neutral-500">Try <b>WELCOME10</b>, <b>FLAT500</b> or <b>VICTORIA25</b>.</p>
                </div>
              )}

              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-neutral-700"><span>Subtotal</span><span>Rs. {subtotal.toLocaleString('en-IN')}.00</span></div>
                <div className="flex justify-between text-[#8b6c3d]"><span>Savings</span><span>-Rs. {savings.toLocaleString('en-IN')}.00</span></div>
                {discount > 0 && <div className="flex justify-between text-green-700 font-medium"><span>Coupon discount</span><span>-Rs. {discount.toLocaleString('en-IN')}.00</span></div>}
                <div className="flex justify-between text-neutral-700"><span>Shipping</span><span>{shipping === 0 ? 'FREE' : `Rs. ${shipping}`}</span></div>
                <div className="flex justify-between text-neutral-900 font-bold text-base pt-2 border-t border-neutral-200"><span>Total</span><span>Rs. {total.toLocaleString('en-IN')}.00</span></div>
              </div>
            </div>

            <div className="mt-4 space-y-2 text-xs text-neutral-600">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#1e3a2b]" /> Secure SSL checkout</div>
              <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#1e3a2b]" /> Free shipping on prepaid orders</div>
              <div className="flex items-center gap-2"><Tag className="w-4 h-4 text-[#1e3a2b]" /> Handmade &bull; Made in India</div>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}
